import { cloneElement, useEffect, useMemo, useState } from "react";
import { Head, router, usePage } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import Drawer from "@/components/drawer";
import { useConfirmDialog } from "@/components/confirm-dialog-provider";
import InvitationDrawerForm from "@/pages/invitations/invitation-drawer-form";
import UserEditDrawer from "@/pages/users/user-edit-drawer";
import { invitation_path, invitations_path, remove_membership_user_path } from "@/routes";

function formatDate(value) {
  if (!value) return "Aldrig";

  return new Intl.DateTimeFormat("da-DK", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function roleLabel(role = "") {
  if (!role) return "Ingen rolle";

  return role.replace(/Role$/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

function UsersIndex({ users = [], invitations = [], onCreateInvitation = null, onEditUser = null }) {
  const { current_tenant } = usePage().props;
  const { confirm } = useConfirmDialog();

  const removeInvitation = async (invitation) => {
    const confirmed = await confirm({
      title: "Fjern invitation",
      description: `Vil du fjerne invitationen til ${invitation.email}?`,
      confirmLabel: "Fjern",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(invitation_path(invitation.token), {
      preserveScroll: true
    });
  };

  const removeMembership = async (user) => {
    const confirmed = await confirm({
      title: "Fjern bruger fra organisation",
      description: `Vil du fjerne ${user.full_name} fra ${current_tenant?.name}?`,
      confirmLabel: "Fjern",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(remove_membership_user_path(user.id), {
      preserveScroll: true
    });
  };

  return (
    <>
      <Head title="Brugere" />

      <section className="workspace-content">
        <section className="card">
          <div className="card-body">
            <div className="workspace-section__eyebrow">Organisation</div>
            <h2 className="workspace-section__title">Brugere i {current_tenant?.name}</h2>
            <p className="workspace-admin-copy">
              Se medlemmer i den aktive organisation og hold styr på, hvem der har adgang til arbejdsrummet.
            </p>

            <div className="workspace-admin-table mt-4">
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Navn</th>
                      <th>E-mail</th>
                      <th>Rolle</th>
                      <th>Seneste login</th>
                      <th className="text-end">Handling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-muted py-4">
                          Der er ingen brugere i organisationen endnu.
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="workspace-admin-name">
                              {user.full_name}
                              {user.current_user ? <span className="workspace-chip">Dig</span> : null}
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <span className="workspace-chip">{roleLabel(user.role)}</span>
                          </td>
                          <td className="text-muted">{formatDate(user.last_sign_in_at)}</td>
                          <td className="text-end">
                            <div className="d-inline-flex gap-2">
                              {user.can_update ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => onEditUser?.(user.id)}
                                >
                                  Redigér
                                </button>
                              ) : null}
                              {user.can_remove_membership ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => removeMembership(user)}
                                >
                                  Fjern
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="workspace-section">
          <section className="card">
            <div className="card-body">
              <div className="workspace-section__eyebrow">Invitationer</div>
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div>
                  <h2 className="workspace-section__title">Afventende invitationer</h2>
                  <p className="workspace-admin-copy mb-0">
                    Invitationer til {current_tenant?.name} som endnu ikke er accepteret.
                  </p>
                </div>
                <span className="workspace-chip">{invitations.length}</span>
              </div>

              <div className="workspace-admin-table mt-4">
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>E-mail</th>
                        <th>Rolle</th>
                        <th>Sendt</th>
                        <th className="text-end">Handling</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-muted py-4">
                            Ingen afventende invitationer.
                          </td>
                        </tr>
                      ) : (
                        invitations.map((invitation) => (
                          <tr key={invitation.id}>
                            <td>{invitation.email}</td>
                            <td>
                              <span className="workspace-chip">{roleLabel(invitation.role)}</span>
                            </td>
                            <td className="text-muted">{formatDate(invitation.created_at)}</td>
                            <td className="text-end">
                              {invitation.can_destroy ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => removeInvitation(invitation)}
                                >
                                  Fjern
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </section>
      </section>
    </>
  );
}

function UsersIndexLayout({ page }) {
  const {
    current_tenant: currentTenant,
    roles = [],
    new_invitation: newInvitation = null,
    create_errors: createErrors = [],
    show_create_invitation_drawer: showCreateInvitationDrawer = false,
    edit_user: editUser = null,
    edit_errors: editErrors = [],
    show_edit_user_drawer: showEditUserDrawer = false,
    users = []
  } = page.props;
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(showCreateInvitationDrawer);
  const [isCreateDrawerSubmitting, setIsCreateDrawerSubmitting] = useState(false);
  const [activeEditUserId, setActiveEditUserId] = useState(editUser?.id || null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(Boolean(showEditUserDrawer && editUser?.id));
  const [isEditDrawerSubmitting, setIsEditDrawerSubmitting] = useState(false);
  const activeEditUser = useMemo(() => {
    if (editUser?.id === activeEditUserId) return editUser;

    return users.find((user) => user.id === activeEditUserId) || null;
  }, [activeEditUserId, editUser, users]);

  useEffect(() => {
    setIsCreateDrawerOpen(showCreateInvitationDrawer);
  }, [showCreateInvitationDrawer]);

  useEffect(() => {
    if (!showEditUserDrawer || !editUser?.id) return;

    setActiveEditUserId(editUser.id);
    setIsEditDrawerOpen(true);
  }, [editUser, showEditUserDrawer]);

  return (
    <AuthLayout
      header={{
        breadcrumbs: [{ label: "Brugere" }],
        title: "Brugere",
        actions: [
          {
            onClick: () => setIsCreateDrawerOpen(true),
            label: "Opret",
            variant: "primary"
          }
        ]
      }}
    >
      {cloneElement(page, {
        onCreateInvitation: () => setIsCreateDrawerOpen(true),
        onEditUser: (userId) => {
          setActiveEditUserId(userId);
          setIsEditDrawerOpen(true);
        }
      })}

      <Drawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        title="Opret bruger"
        size="md"
        closeDisabled={isCreateDrawerSubmitting}
      >
        <InvitationDrawerForm
          submitPath={invitations_path()}
          invitation={newInvitation}
          errors={createErrors}
          currentTenant={currentTenant}
          roles={roles}
          organizationLocked={true}
          onSuccess={() => setIsCreateDrawerOpen(false)}
          onProcessingChange={setIsCreateDrawerSubmitting}
        />
      </Drawer>

      <Drawer
        isOpen={Boolean(activeEditUser) && isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false);
          setActiveEditUserId(null);
        }}
        title={activeEditUser ? `Redigér ${activeEditUser.full_name}` : "Redigér bruger"}
        size="md"
        closeDisabled={isEditDrawerSubmitting}
      >
        {activeEditUser ? (
          <UserEditDrawer
            user={activeEditUser}
            roles={roles}
            errors={showEditUserDrawer && editUser?.id === activeEditUser.id ? editErrors : []}
            onSuccess={() => {
              setIsEditDrawerOpen(false);
              setActiveEditUserId(null);
            }}
            onProcessingChange={setIsEditDrawerSubmitting}
          />
        ) : null}
      </Drawer>
    </AuthLayout>
  );
}

UsersIndex.layout = (page) => <UsersIndexLayout page={page} />;

export default UsersIndex;
